/**
 * S9. Build failure, and copy for agent.
 *
 * The highest leverage novice feature in the product: turn a build failure
 * into one paste into a coding agent.
 *
 * ZONE ORDER IS THE DESIGN, and it is Netlify's lesson taken seriously: the
 * diagnosis renders above the log, never inside it. And before the diagnosis
 * comes the one fact every competitor buries, which is that the site is still
 * up. A person whose deploy just failed is asking "did I break it", not "what
 * is a TS2551", and the screen answers in that order:
 *
 *   1  is my app still live          (reassurance, and it is not optional)
 *   2  what failed                   (the deploy, its identity, its steps)
 *   3  why, and one button           (the error block, spec 4.3)
 *   4  what happens now              (three facts, no action needed)
 *
 * THE PAYLOAD IS VERBATIM. Screen spec 5.6 writes the fix request exactly, and
 * the three things that make it work are the three a tidy up would remove: it
 * names the product and the situation before it names the error, it carries
 * the code context lines rather than just the message, and it closes by
 * telling the agent not to deploy. That last line is the most valuable
 * sentence in the payload, and only the platform knows to say it.
 *
 * WHAT'S IN THIS? sits beside the button and shows the exact payload. Nina
 * will not paste a black box into her agent, and Priya will not trust one.
 *
 * THIS SCREEN HAS NO ADVANCED LAYER OF ITS OWN, AND THAT IS DELIBERATE. It
 * inherits one, from the error block's "Show the build log" disclosure, which
 * is one click away in both modes. It needs nothing more: "What's in this?"
 * already puts the raw payload on the screen in Simple, so the technical
 * detail a person could want here is already reachable without a switch.
 * Recorded so the next audit stops asking where the disclosure is.
 *
 * FAKEABLE STATES, deep linkable per prototype mechanic (a):
 *   ?state=no-diagnosis   the honest degraded case: we could not read the log
 *   ?state=never-live     the app has never been live, so zone 1 changes
 * The retry's outcome comes from the demo dock's "next build fails" switch, so
 * both endings are reachable without a second URL.
 */

import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { AsyncNarration, StepLedger } from '../../components/patterns/AsyncNarration';
import type { NarrationStep } from '../../components/patterns/AsyncNarration';
import { ErrorBlock } from '../../components/patterns/ErrorBlock';
import { Lamp, StatusPill } from '../../components/patterns/StatusPill';
import { whenLabel, dateLabel } from '../../components/patterns/DeployRow';

import { environmentOf, getDeploy, getProject, listDeploys } from '../../lib/api';
import { durationSeconds, shortSha } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useTicker } from '../../lib/hooks/use-ticker';
import { t } from '../../lib/i18n/t';
import { stateOf } from '../../lib/mock/simulate';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { Deploy } from '../../lib/types';

import '../../styles/components/build-failure.css';

/** The log tail the degraded payload carries. Screen spec S9, no diagnosis. */
const TAIL_LINES = 60;

/**
 * The payload for the case where we could not work out what went wrong. It is
 * built rather than seeded because it is a function of the log, and it is
 * still the primary action: an agent reading sixty lines of build output will
 * usually beat a person reading them, which is exactly why the button stays
 * where it is instead of being demoted for being less certain.
 *
 * Like the seeded payload, it is machine text and does not go through t().
 */
function fallbackFixRequest(
  projectName: string,
  deploy: Deploy,
  failedStepLabel: string,
  log: string,
): string {
  const tail = log
    .split('\n')
    .slice(-TAIL_LINES)
    .map((line) => `  ${line}`)
    .join('\n');

  return `My app is hosted on SV Cloud. A deploy just failed and I need you to fix it.

Project: ${projectName}
Deploy: ${deploy.commitSha} "${deploy.commitMessage}"
Build step that failed: ${failedStepLabel}

SV Cloud could not work out the cause. These are the last ${TAIL_LINES} lines of the build log:

${tail}

Please find the cause, fix it in my code, and commit the fix to main. Pushing
to main deploys automatically, so do not run any deploy commands.`;
}

export default function BuildFailure() {
  const slug = useProjectSlug() ?? '';
  const { id = '' } = useParams();
  const { search } = useLocation();
  const state = new URLSearchParams(search).get('state');
  const [advanced] = useAdvanced();

  const back = `/p/${slug}/deploys`;

  const { data, isLoading } = useAsync(async () => {
    const project = await getProject(slug);
    const deploy = await getDeploy(id);
    const deploys = project ? await listDeploys(project.id) : [];
    return { project, deploy, deploys };
  }, [slug, id]);

  const timelines = useDemoStore((store) => store.timelines);
  const startTimeline = useDemoStore((store) => store.startTimeline);
  const nextBuildFails = useDemoStore((store) => store.prefs.nextBuildFails);
  const now = useTicker();
  const [retrying, setRetrying] = useState(false);
  const retryLine = timelines.find((one) => one.id === `retry-${id}`);
  const retryState = retryLine ? stateOf(retryLine, now) : null;

  useEffect(() => {
    const project = data?.project ?? null;
    const deploy = data?.deploy ?? null;
    if (deploy && project && deploy.status === 'didnt-work') {
      const diagnosis = deploy.diagnosis ?? null;
      const readable = state !== 'no-diagnosis' && diagnosis?.summary != null;
      const summary = readable
        ? t(diagnosis?.summary ?? '')
        : t("We couldn't work out what went wrong from the build log.");
      captureEvent('build_failure_viewed', {
        project_slug: project.slug,
        deploy_id: deploy.id,
        summary,
        readable,
        location: diagnosis?.location,
      });
    }
  }, [data, state]);

  if (isLoading) return <div className="page" />;

  const project = data?.project ?? null;
  const deploy = data?.deploy ?? null;
  if (!project || !deploy) return <Navigate to={back} replace />;

  // A deploy that worked is not a failure screen. It has its own record.
  if (deploy.status !== 'didnt-work') {
    return <Navigate to={`${back}/${deploy.id}`} replace />;
  }

  const environment = environmentOf(project);
  const liveDeploy = (data?.deploys ?? []).find((one) => one.status === 'live') ?? null;
  const neverLive = state === 'never-live' || liveDeploy === null;

  const steps: NarrationStep[] = (deploy.steps ?? []).map((step) => ({
    label: t(step.label),
    status: step.status,
    elapsed: step.ms > 0 ? durationSeconds(step.ms) : undefined,
  }));

  const failedStep = (deploy.steps ?? []).find((step) => step.status === 'failed');
  const failedStepLabel = failedStep ? t(failedStep.label) : t('Build your app');

  const diagnosis = deploy.diagnosis ?? null;
  const readable = state !== 'no-diagnosis' && diagnosis?.summary != null;

  const summary = readable
    ? t(diagnosis?.summary ?? '')
    : t("We couldn't work out what went wrong from the build log.");

  const cause = readable
    ? t(diagnosis?.cause ?? '')
    : t(
        'The last lines of the build log are in the fix request, so your coding agent can read them and work it out. Nothing changed on your live site.',
      );

  const fixRequest =
    readable && diagnosis
      ? diagnosis.fixRequest
      : fallbackFixRequest(project.name, deploy, failedStepLabel, deploy.rawLog ?? '');

  const errorBlock = (
    <ErrorBlock
      summary={summary}
      cause={cause}
      location={readable ? (diagnosis?.location ?? undefined) : undefined}
      fixRequest={fixRequest}
      contextAction={{
        label: t('Try this deploy again'),
        onAct: () => {
          captureEvent('build_failure_retry_clicked', {
            project_slug: project?.slug,
            deploy_id: deploy?.id,
          });
          setRetrying(true);
          startTimeline({
            id: `retry-${id}`,
            kind: 'redeploy',
            scriptId: 'redeploy',
            subjectId: deploy.id,
            startedAt: Date.now(),
            outcome: nextBuildFails ? 'fail@build' : 'success',
          });
        },
      }}
      rawLabel={t('Show the build log')}
      raw={deploy.rawLog}
    />
  );

  const retryWorked = retrying && retryState?.status === 'succeeded';

  return (
    <div className="page bfail">
      <Link className="bfail__back" to={back}>
        <span className="bfail__back-glyph" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
            <path
              d="M10 3.5 5.5 8l4.5 4.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {t('Deploys')}
      </Link>

      {/* ZONE 1. It comes first and it is not optional. The single most useful
          thing to tell a first time builder whose build just failed is that
          their site is still up, and every competitor buries it. */}
      <section className="bfail__calm" aria-label={t('What this means for your app')}>
        {neverLive ? (
          <p className="bfail__calm-line">
            {t("Your app isn't live yet. This was its first deploy.")}
          </p>
        ) : (
          <>
            <p className="bfail__calm-head">
              <Lamp value="live" />
              {t('Your app is still live')}
            </p>
            <p className="bfail__calm-line">
              {t(
                "This deploy didn't work, so your app kept running the version from {date}. Nobody visiting your site noticed.",
                { date: dateLabel(liveDeploy?.createdAt ?? deploy.createdAt) },
              )}
            </p>
          </>
        )}
      </section>

      {/* ZONE 2. What failed, and how far it got. Two of five steps did work,
          and saying so is materially different from saying "it failed". */}
      <header className="bfail__head">
        <StatusPill value="didnt-work" />
        <h1 className="bfail__title">{deploy.commitMessage}</h1>
        {/* The hash is an identifier, so it follows the same rule it follows
            on the projects list, the deploys timeline and the live plate:
            Advanced, appended, never printed bare in Simple. The same fact
            cannot be Advanced on one screen and Simple on the next. */}
        <p className="bfail__meta">
          <span>{deploy.author}</span>
          {advanced ? <span className="mono">{shortSha(deploy.commitSha)}</span> : null}
          <span>{whenLabel(deploy.createdAt)}</span>
          <span>
            {Math.round((deploy.durationMs ?? 0) / 1000) === 1
              ? t('failed after 1 second')
              : t('failed after {n} seconds', {
                  n: Math.round((deploy.durationMs ?? 0) / 1000),
                })}
          </span>
        </p>
      </header>

      <section className="bfail__steps" aria-label={t('What the build did')}>
        <StepLedger steps={steps} />
      </section>

      {/* ZONE 3. The diagnosis, above the log, never inside it. */}
      {retrying && retryLine ? (
        <section className="bfail__retry" aria-label={t('Trying this deploy again')}>
          <AsyncNarration timeline={retryLine} error={errorBlock} />
          {retryWorked ? (
            <div className="bfail__worked">
              <p className="bfail__worked-line">
                <Lamp value="live" />
                {t('That worked. Your app is running the new version.')}
              </p>
              <Button
                variant="solid"
                onClick={() => window.open(environment.webAddress, '_blank', 'noreferrer')}
              >
                {t('Visit your app')}
              </Button>
            </div>
          ) : null}
        </section>
      ) : (
        errorBlock
      )}

      {/* ZONE 4. What happens now, in facts, with nothing to do about any of
          them. Every failure message in this product either names an action or
          says plainly that none is needed. This one says none is needed. */}
      <section className="bfail__next" aria-label={t('What happens now')}>
        <ul className="bfail__next-list">
          <li className="bfail__next-item">
            {neverLive
              ? t('Nothing is live yet, so nothing about your web address changed.')
              : t('Your app keeps running the version from {date}.', {
                  date: dateLabel(liveDeploy?.createdAt ?? deploy.createdAt),
                })}
          </li>
          <li className="bfail__next-item">{t('Your database and your files did not change.')}</li>
          <li className="bfail__next-item">
            {t('Your next push to main tries again on its own.')}
          </li>
        </ul>
      </section>
    </div>
  );
}
